import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import axios from '../../utility/customAxios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { SingleSelectorSearchableDropdown } from '../Input/SingleSelectorSearchableDropdown';
import { DropDownItem } from '../Input/MultiSelectorDropdown/MultiSelectorDropDown';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { toast } from '../ui/use-toast';
import { Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

// Repository location options
const REPOSITORY_LOCATIONS = [
    { value: 'github', label: 'GitHub' },
];

// Form validation schema
const sourceCodeLinkingSchema = z.object({
    serviceName: z.string().min(1, 'Service is required'),
    repositoryLocation: z.string().min(1, 'Repository location is required'),
    repositoryUrl: z.string().min(1, 'Repository is required'),
    servicePath: z.string().optional(),
});

type FormValues = z.infer<typeof sourceCodeLinkingSchema>;

interface KnownService {
    displayName: string;
    serviceName: string;
    requestsPerSecond: number;
    logsPerSecond: number;
}

interface GithubRepository {
    id: number;
    name: string;
    full_name: string;
    url: string;
    html_url: string;
    private: boolean;
}

interface SourceCodeLinkingFormProps {
    serviceName?: string; // Optional pre-populated service name
    onSuccess?: () => void; // Callback on successful submission
    onCancel?: () => void; // Callback for cancel action
    editMode?: boolean; // Whether in edit mode
    currentRepositoryUrl?: string; // Current repository URL for edit mode
    currentServicePath?: string; // Current service path for edit mode
}

export const SourceCodeLinkingForm: React.FC<SourceCodeLinkingFormProps> = ({ 
    serviceName, 
    onSuccess, 
    onCancel,
    editMode = false,
    currentRepositoryUrl,
    currentServicePath 
}) => {
    const [knownServices, setKnownServices] = useState<KnownService[]>([]);
    const [repositories, setRepositories] = useState<GithubRepository[]>([]);
    const [repositoryItems, setRepositoryItems] = useState<DropDownItem[]>([]);
    const [selectedRepositoryItem, setSelectedRepositoryItem] = useState<DropDownItem>({
        displayName: 'Select a repository',
        value: ''
    });
    const [loading, setLoading] = useState(false);
    const [loadingServices, setLoadingServices] = useState(false);
    const [loadingRepositories, setLoadingRepositories] = useState(false);
    const [searchParams] = useSearchParams();
    
    // Get the current environment from URL params
    const currentEnvironment = searchParams.get("environment") || null;

    const form = useForm<FormValues>({
        resolver: zodResolver(sourceCodeLinkingSchema),
        defaultValues: {
            serviceName: serviceName || '',
            repositoryLocation: 'github',
            repositoryUrl: currentRepositoryUrl || selectedRepositoryItem.value || '',
            servicePath: currentServicePath || '',
        },
    });

    // Fetch known services
    useEffect(() => {
        const fetchKnownServices = async () => {
            if (!currentEnvironment) return;
            
            setLoadingServices(true);
            try {
                const response = await axios.get(`/api/v1/knownServices?environment=${encodeURIComponent(currentEnvironment)}`);
                if (response.data && response.data.services) {
                    setKnownServices(response.data.services);
                }
            } catch (error) {
                console.error('Failed to fetch known services:', error);
                toast({
                    title: 'Error',
                    description: 'Failed to load services. Please try again.',
                    variant: 'destructive',
                });
            } finally {
                setLoadingServices(false);
            }
        };

        fetchKnownServices();
    }, [currentEnvironment]);

    // Fetch repositories when repository location changes to github
    useEffect(() => {
        const fetchRepositories = async () => {
            if (form.watch('repositoryLocation') !== 'github') {
                return;
            }

            setLoadingRepositories(true);
            try {
                const response = await axios.get('/api/v1/source/repositories/list');
                if (response.data && response.data.data && response.data.data.repositories) {
                    const repos = response.data.data.repositories;
                    setRepositories(repos);
                    
                    // Convert repositories to DropDownItem format
                    const items: DropDownItem[] = repos.map((repo: GithubRepository) => ({
                        displayName: repo.full_name,
                        value: repo.html_url
                    }));
                    setRepositoryItems(items);
                }
            } catch (error) {
                console.error('Failed to fetch repositories:', error);
                toast({
                    title: 'Error',
                    description: 'Failed to load repositories. Please try again.',
                    variant: 'destructive',
                });
            } finally {
                setLoadingRepositories(false);
            }
        };

        fetchRepositories();
    }, [form.watch('repositoryLocation')]);

    // Set initial repository selection for edit mode
    useEffect(() => {
        if (editMode && currentRepositoryUrl && repositoryItems.length > 0) {
            const matchingItem = repositoryItems.find(item => item.value === currentRepositoryUrl);
            if (matchingItem) {
                setSelectedRepositoryItem(matchingItem);
            } else {
                // If the current URL doesn't match any item, create a custom one
                setSelectedRepositoryItem({
                    displayName: currentRepositoryUrl,
                    value: currentRepositoryUrl
                });
            }
        }
    }, [editMode, currentRepositoryUrl, repositoryItems]);
    
    // Update form value when repository selection changes
    useEffect(() => {
        if (selectedRepositoryItem.value && selectedRepositoryItem.value !== '') {
            form.setValue('repositoryUrl', selectedRepositoryItem.value);
        }
    }, [selectedRepositoryItem, form]);

    const onSubmit = async (data: FormValues) => {
        setLoading(true);
        try {
            await axios.post('/api/v1/source/repository/save', {
                serviceId: data.serviceName,
                sourceRepositoryLocation: data.repositoryLocation,
                sourceRepository: data.repositoryUrl,
                servicePath: data.servicePath,
            });

            toast({
                title: 'Success',
                description: 'Source code linked successfully',
            });

            form.reset();
            onSuccess?.();
        } catch (error: any) {
            console.error('Failed to link source code:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.error || 'Failed to link source code. Please try again.',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                    control={form.control}
                    name="serviceName"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Service</FormLabel>
                            <FormControl>
                                {serviceName ? (
                                    <Input {...field} disabled className={"h-[36px]"} />
                                ) : (
                                    <Select
                                        value={field.value}
                                        onValueChange={field.onChange}
                                        disabled={loadingServices}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select a service" className={"h-[36px]"} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {knownServices.map((service) => (
                                                <SelectItem key={service.serviceName} value={service.serviceName}>
                                                    {service.displayName}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            </FormControl>
                            <FormDescription className={"text-textdark"}>
                                {serviceName 
                                    ? 'Service is pre-selected for this context' 
                                    : 'Select the service you want to link to source code'}
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="repositoryLocation"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Repository Location</FormLabel>
                            <FormControl>
                                <Select value={field.value} onValueChange={field.onChange}>
                                    <SelectTrigger className={"border border-border rounded"}>
                                        <SelectValue placeholder="Select repository location" />
                                    </SelectTrigger>
                                    <SelectContent className={"text-textmedium bg-backgroundmedium border border-border rounded"}>
                                        {REPOSITORY_LOCATIONS.map((location) => (
                                            <SelectItem key={location.value} value={location.value}>
                                                {location.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormDescription className={"text-textdark"}>
                                Select where your source code is hosted
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="repositoryUrl"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Repository</FormLabel>
                            <FormControl>
                                {form.watch('repositoryLocation') === 'github' ? (
                                    <SingleSelectorSearchableDropdown
                                        displaySettings={{
                                            smallSize: false,
                                            borderClassName: "border border-border rounded",
                                            className: "bg-backgroundmedium text-sm h-[36px]"
                                        }}
                                        noMatchString="No matching repositories found"
                                        possibleItems={repositoryItems}
                                        searchPlaceholder="Search repositories..."
                                        selected={selectedRepositoryItem}
                                        setSelected={setSelectedRepositoryItem}
                                        disableWildcard={true}
                                    />
                                ) : (
                                    <div className="flex items-center justify-between text-textmedium px-2 text-xl h-[48px] border border-border rounded bg-backgroundmedium opacity-50">
                                        <span className="text-sm">Repository selection is only available for GitHub</span>
                                    </div>
                                )}
                            </FormControl>
                            <FormDescription className={"text-textdark"}>
                                {form.watch('repositoryLocation') === 'github' 
                                    ? 'Select the repository from your GitHub account'
                                    : 'Repository selection is only available for GitHub'}
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="servicePath"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Service Path (Optional)</FormLabel>
                            <FormControl>
                                <Input 
                                    {...field} 
                                    placeholder="e.g., /services/my-service"
                                    className="border border-border rounded bg-backgroundmedium h-[36px]"
                                />
                            </FormControl>
                            <FormDescription className={"text-textdark"}>
                                Path to the service within the repository (for monorepos)
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <div className="flex justify-end space-x-2">
                    {onCancel && (
                        <Button type="button"  onClick={onCancel} className={"border border-red-500 rounded bg-red-500/10  hover:text-textlight"}>
                            Cancel
                        </Button>
                    )}
                    <Button type="submit" disabled={loading} className={"border border-primary rounded bg-primarytransparent hover:text-textlight"}>
                        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {editMode ? 'Update Source Code' : 'Link Source Code'}
                    </Button>
                </div>
            </form>
        </Form>
    );
};